import React, { useEffect, useState } from "react";
import { Link, useParams, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { toastr } from "react-redux-toastr";
import { API_URL } from "../../utils/constant";

// Custom styles matching vendor risk page
const actionButtonsStyle = `
.action-buttons {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
}

.action-btn {
    min-width: 80px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    color: #6563ff;
    transition: all 0.2s ease;
    background: transparent;
    border: 1px solid #e0e0e0;
    text-decoration: none;
    padding: 0 8px;
    font-size: 12px;
    font-weight: 500;
}

.action-btn:hover {
    background: #f8f9fa;
    color: #4a47a7;
    text-decoration: none;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.action-btn.text-danger:hover {
    background: #fff5f5;
    color: #dc3545;
    border-color: #dc3545;
}

.action-btn.text-info:hover {
    background: #f0f9ff;
    color: #0dcaf0;
    border-color: #0dcaf0;
}

.action-btn.text-success:hover {
    background: #f0fff4;
    color: #28a745;
    border-color: #28a745;
}

.action-btn i {
    font-size: 12px;
    margin-right: 4px;
}

.action-label {
    font-size: 11px;
    font-weight: 500;
    white-space: nowrap;
}

.table-responsive.table-risk {
    margin-top: 20px;
}

.filter-section {
    background: #f8f9fa;
    padding: 20px;
    border-radius: 8px;
    margin-bottom: 20px;
}

/* Responsive design for smaller screens */
@media (max-width: 768px) {
    .action-buttons {
        gap: 4px;
    }
    
    .action-btn {
        min-width: 60px;
        height: 28px;
        font-size: 11px;
        padding: 0 6px;
    }
    
    .action-btn i {
        font-size: 11px;
        margin-right: 3px;
    }
    
    .action-label {
        font-size: 10px;
    }
}
`;

const RedTeamingDashboard = () => {
  const history = useHistory();
  
  // State Management
  const [dashboardData, setDashboardData] = useState({
    summary: {
      totalIssues: 0,
      totalCriticalSeverityIssues: 0,
      totalHighSeverityIssues: 0,
      totalMediumSeverityIssues: 0,
      totalLowSeverityIssues: 0,
    },
    issues: [],
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      limit: 5,
      hasNextPage: false,
      hasPrevPage: false
    },
    chartData: {
      issuesBySeverity: []
    }
  });
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState("");
  const [selectedIssue, setSelectedIssue] = useState(null);

  // Modal handlers
  const handleShowModal = (issue) => {
    setSelectedIssue(issue);
    setIsModalOpen("issue-modal");
  };

  const handleCloseModal = () => {
    setIsModalOpen("");
    setSelectedIssue(null);
  };

  const { orgId } = useParams();
  const vendorId = sessionStorage.getItem('vendorId');
  const id = useSelector(state => {
    if (orgId) {
      return orgId;
    } else {
      return state?.summary?.localClient?._id || state?.summary?.clients?.[0]?._id;
    }
  });

  const handleApiError = (error, defaultMessage = "An error occurred") => {
    // console.error removed
    let message = defaultMessage;

    if (error.response) {
      message = error.response.data?.message || error.response.data?.userMessage || message;
    } else if (error.message) {
      message = error.message;
    }

    toastr.error('Error', message);
  };

  const handleTotalTestsClick = () => {
    history.push("/red-teaming/scans");
  };

  const fetchIssuesTotals = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/organisations/${id}/cart/dashboard/issues-totals`
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "Failed to fetch issues totals");
      return {
        success: false,
        data: {
          totalHighSeverityIssues: 0,
          totalMediumSeverityIssues: 0,
          totalLowSeverityIssues: 0,
          totalCriticalSeverityIssues: 0,
          totalIssues: 0
        }
      };
    }
  };

  const fetchTotalTestsPerformed = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/organisations/${id}/cart/dashboard/total-tests-performed`
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "Failed to fetch total tests performed");
      return {
        success: false,
        data: {
          totalTestsPerformed: 0
        }
      };
    }
  };

  const fetchTotalStimulations = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/organisations/${id}/cart/dashboard/total-stimulations`
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "Failed to fetch total stimulations");
      return {
        success: false,
        data: {
          totalStimulations: 0
        }
      };
    }
  };

  const fetchIssues = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/organisations/${id}/cart/issues`,
        {
          params: {
            limit: 5,
            page: 1,
            sortBy: 'updatedAt',
            sortOrder: 'desc'
          }
        }
      );

      if (response.data?.success && response.data?.data) {
        return {
          success: true,
          data: {
            issues: response.data.data.issues || [],
            pagination: response.data.data.pagination || {
              currentPage: 1,
              totalPages: 1,
              totalCount: 0,
              limit: 5,
              hasNextPage: false,
              hasPrevPage: false
            }
          }
        };
      }

      return {
        success: false,
        data: {
          issues: [],
          pagination: {
            currentPage: 1,
            totalPages: 1,
            totalCount: 0,
            limit: 5,
            hasNextPage: false,
            hasPrevPage: false
          }
        }
      };
    } catch (error) {
      handleApiError(error, "Failed to fetch issues");
      return {
        success: false,
        data: {
          issues: [],
          pagination: {
            currentPage: 1,
            totalPages: 1,
            totalCount: 0,
            limit: 5,
            hasNextPage: false,
            hasPrevPage: false
          }
        }
      };
    }
  };

  const fetchCartDashboard = async () => {
    try {
      setLoading(true);
      if (!id) {
        toastr.error('Error', 'Organisation ID not found');
        return;
      }

      const [
        issuesResponse,
        issuesTotalsResponse,
        testsResponse,
        stimulationsResponse
      ] = await Promise.all([
        fetchIssues(),
        fetchIssuesTotals(),
        fetchTotalTestsPerformed(),
        fetchTotalStimulations()
      ]);

      const issues = issuesResponse.success ? issuesResponse.data?.issues || [] : [];
      const pagination = issuesResponse.success ? issuesResponse.data?.pagination || {} : {};
      const issuesTotals = issuesTotalsResponse.success ? issuesTotalsResponse.data : {};
      const totalTests = testsResponse.success ? testsResponse.data?.totalTestsPerformed || 0 : 0;
      const totalStimulations = stimulationsResponse.success ? stimulationsResponse.data?.totalStimulations || 0 : 0;

      setDashboardData({
        summary: {
          totalIssues: issuesTotals.totalIssues || 0,
          totalCriticalSeverityIssues: issuesTotals.totalCriticalSeverityIssues || 0,
          totalHighSeverityIssues: issuesTotals.totalHighSeverityIssues || 0,
          totalMediumSeverityIssues: issuesTotals.totalMediumSeverityIssues || 0,
          totalLowSeverityIssues: issuesTotals.totalLowSeverityIssues || 0,
          totalTestsPerformed: totalTests,
          totalStimulations: totalStimulations
        },
        issues: issues,
        pagination: pagination,
        chartData: {
          issuesBySeverity: [
            { severity: 'Critical', count: issuesTotals.totalCriticalSeverityIssues || 0 },
            { severity: 'High', count: issuesTotals.totalHighSeverityIssues || 0 },
            { severity: 'Medium', count: issuesTotals.totalMediumSeverityIssues || 0 },
            { severity: 'Low', count: issuesTotals.totalLowSeverityIssues || 0 }
          ]
        }
      });

    } catch (error) {
      handleApiError(error, "Failed to fetch dashboard data");
      setDashboardData({
        summary: {
          totalIssues: 0,
          totalCriticalSeverityIssues: 0,
          totalHighSeverityIssues: 0,
          totalMediumSeverityIssues: 0,
          totalLowSeverityIssues: 0,
          totalTestsPerformed: 0,
          totalStimulations: 0
        },
        issues: [],
        pagination: {
          currentPage: 1,
          totalPages: 1,
          totalCount: 0,
          limit: 5,
          hasNextPage: false,
          hasPrevPage: false
        },
        chartData: {
          issuesBySeverity: []
        }
      });
    } finally {
      setLoading(false);
    }
  };

  // Effects
  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.textContent = actionButtonsStyle;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  useEffect(() => {
    if (id) {
      fetchCartDashboard();
    }
  }, [id]);

  return (
    <div className="container-fluid">
      <div className="block-header">
        <div className="row clearfix">
          <div className="col-md-6 col-sm-12">
            <h1 className="welcome-text">Red Teaming Dashboard</h1>
            <nav aria-label="breadcrumb" className="mt-1">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  {vendorId ? <>
                    <Link to="/vendor"
                      onClick={(() => {
                        sessionStorage.clear();
                      })}
                    >Vendor Assessment</Link>
                  </> : <Link to="/" className="welcome-textSmall">Home</Link>}
                </li>
                <li
                  className="breadcrumb-item active"
                  aria-current="page"
                  style={{ color: "white" }}
                >
                  Red Teaming Dashboard
                </li>
              </ol>
              <br />
            </nav>
          </div>
        </div>
      </div>

      {/* Summary Cards Row 1 - Issues Overview */}
      <div className="row">
        <div className="col-xl-3 col-md-6">
          <div 
            className="card" 
            onClick={() => history.push("/red-teaming/issues")}
            style={{ cursor: 'pointer' }}
          >
            <div className="card-body">
              <div className="row">
                <div className="col-6">
                  <div className="avatar-sm rounded bg-soft-primary float-left">
                    <i className="fa fa-shield-alt font-20 avatar-title"></i>
                  </div>
                </div>
                <div className="col-6">
                  <div className="text-right">
                    <h3 className="text-dark my-1">
                      <span data-plugin="counterup">
                        {loading ? '...' : (dashboardData?.summary?.totalIssues || 0)}
                      </span>
                    </h3>
                    <p className="text-muted mb-1 text-truncate">Total Issues</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6">
          <div 
            className="card" 
            onClick={() => history.push("/red-teaming/issues")}
            style={{ cursor: 'pointer' }}
          >
            <div className="card-body">
              <div className="row">
                <div className="col-6">
                  <div className="avatar-sm rounded bg-soft-danger float-left">
                    <i className="fa fa-fire font-20 avatar-title"></i>
                  </div>
                </div>
                <div className="col-6">
                  <div className="text-right">
                    <h3 className="text-dark my-1">
                      <span data-plugin="counterup">
                        {loading ? '...' : (dashboardData?.summary?.totalCriticalSeverityIssues || 0)}
                      </span>
                    </h3>
                    <p className="text-muted mb-1 text-truncate">Critical Issues</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6">
          <div 
            className="card" 
            onClick={() => history.push("/red-teaming/issues")}
            style={{ cursor: 'pointer' }}
          >
            <div className="card-body">
              <div className="row">
                <div className="col-6">
                  <div className="avatar-sm rounded bg-soft-warning float-left">
                    <i className="fa fa-exclamation-triangle font-20 avatar-title"></i>
                  </div>
                </div>
                <div className="col-6">
                  <div className="text-right">
                    <h3 className="text-dark my-1">
                      <span data-plugin="counterup">
                        {loading ? '...' : (dashboardData?.summary?.totalHighSeverityIssues || 0)}
                      </span>
                    </h3>
                    <p className="text-muted mb-1 text-truncate">High Issues</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6">
          <div 
            className="card" 
            onClick={() => history.push("/red-teaming/issues")}
            style={{ cursor: 'pointer' }}
          >
            <div className="card-body">
              <div className="row">
                <div className="col-6">
                  <div className="avatar-sm rounded bg-soft-info float-left">
                    <i className="fa fa-exclamation-circle font-20 avatar-title"></i>
                </div>
                </div>
                <div className="col-6">
                  <div className="text-right">
                    <h3 className="text-dark my-1">
                      <span data-plugin="counterup">
                        {loading ? '...' : (dashboardData?.summary?.totalMediumSeverityIssues || 0)}
                      </span>
                    </h3>
                    <p className="text-muted mb-1 text-truncate">Medium Issues</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards Row 2 - Additional Metrics */}
      <div className="row mt-3">
        <div className="col-xl-3 col-md-6">
          <div 
            className="card" 
            onClick={() => history.push("/red-teaming/issues")}
            style={{ cursor: 'pointer' }}
          >
            <div className="card-body">
              <div className="row">
                <div className="col-6">
                  <div className="avatar-sm rounded bg-soft-success float-left">
                    <i className="fa fa-check-circle font-20 avatar-title text-success"></i>
                  </div>
                </div>
                <div className="col-6">
                  <div className="text-right">
                    <h3 className="text-dark my-1">
                      <span data-plugin="counterup">
                        {loading ? '...' : (dashboardData?.summary?.totalLowSeverityIssues || 0)}
                      </span>
                    </h3>
                    <p className="text-muted mb-1 text-truncate">Low Issues</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6">
          <div 
            className="card" 
            onClick={handleTotalTestsClick}
            style={{ cursor: 'pointer' }}
          >
            <div className="card-body">
              <div className="row">
                <div className="col-6">
                  <div className="avatar-sm rounded bg-soft-primary float-left">
                    <i className="fa fa-play-circle font-20 avatar-title text-primary"></i>
                  </div>
                </div>
                <div className="col-6">
                  <div className="text-right">
                    <h3 className="text-dark my-1">
                      <span data-plugin="counterup">
                        {loading ? '...' : (dashboardData?.summary?.totalStimulations || 0)}
                      </span>
                    </h3>
                    <p className="text-muted mb-1 text-truncate">Total Simulation</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6">
          <div 
            className="card" 
            onClick={handleTotalTestsClick}
            style={{ cursor: 'pointer' }}
          >
            <div className="card-body">
              <div className="row">
                <div className="col-6">
                  <div className="avatar-sm rounded bg-soft-info float-left">
                    <i className="fa fa-tasks font-20 avatar-title text-info"></i>
                  </div>
                </div>
                <div className="col-6">
                  <div className="text-right">
                    <h3 className="text-dark my-1">
                      <span data-plugin="counterup">
                        {loading ? '...' : (dashboardData?.summary?.totalTestsPerformed || 0)}
                      </span>
                    </h3>
                    <p className="text-muted mb-1 text-truncate">Total Test Performed</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Issues Table */}
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="card-title">Recent Issues</h4>
                <Link 
                  to="/red-teaming/issues" 
                  className="btn btn-primary"
                >
                  View All Issues ({dashboardData.pagination.totalCount || 0})
                </Link>
              </div>
              <div className="table-responsive table-risk">
                <table className="table mb-0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Issue Name</th>
                      <th>Target</th>
                      <th>Simulation Name</th>
                      <th>Severity</th>
                      <th>Created At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData?.issues?.length > 0 ? (
                      dashboardData.issues.map((issue, index) => (
                        <tr key={issue.issueId}>
                          <td>
                            <span>{index + 1}</span>
                          </td>
                          <td>{issue.issueName}</td>
                          <td>{issue.target}</td>
                          <td>
                            <span className="badge badge-info">
                              {issue.stimulationName}
                            </span>
                          </td>
                          <td>
                            <span className={`badge badge-${issue.issueSeverity.toLowerCase()}`}>
                              {issue.issueSeverity.toUpperCase()}
                            </span>
                          </td>
                          <td>
                            {new Date(issue.createdAt).toLocaleString()}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center text-muted py-4">
                          <i className="fa fa-inbox fa-2x mb-2"></i>
                          <p>No issues found</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Issue Details Modal */}
      <div
        className={`modal fade Form-Wizard-modal ${isModalOpen === "issue-modal" ? "d-block show" : ""}`}
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Issue Details</h5>
              <button
                type="button"
                data-dismiss="modal"
                aria-label="Close"
                onClick={handleCloseModal}
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <div className="modal-body">
              {selectedIssue && (
                <div className="row">
                  <div className="col-12">
                    <h6>Issue Name</h6>
                    <p>{selectedIssue.issueName}</p>

                    <h6>Description</h6>
                    <p>{selectedIssue.issueDescription}</p>

                    <h6>Target</h6>
                    <p>{selectedIssue.target} ({selectedIssue.targetType})</p>

                    <h6>Simulation Name</h6>
                    <p>{selectedIssue.stimulationName}</p>

                    <h6>Severity</h6>
                    <span className={`badge badge-${selectedIssue.issueSeverity.toLowerCase()}`}>
                      {selectedIssue.issueSeverity.toUpperCase()}
                    </span>

                    {selectedIssue.recommendation && (
                      <>
                        <h6 className="mt-3">Recommendation</h6>
                        <p>{selectedIssue.recommendation}</p>
                      </>
                    )}

                    <h6 className="mt-3">Created At</h6>
                    <p>{new Date(selectedIssue.createdAt).toLocaleString()}</p>

                    <h6>Updated At</h6>
                    <p>{new Date(selectedIssue.updatedAt).toLocaleString()}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RedTeamingDashboard; 